import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TextField,
  Box,
  Divider,
  MenuItem,
  Grid2,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  Button,
  OutlinedInput,
  Alert,
  FormHelperText,
} from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";

import { AxiosError } from "axios";
import { Driver } from "@lib/types/driver";
import useHandleError from "@lib/utils/errorHook";
import { getDrivers } from "@lib/api/queries/drivers";

import Autocomplete from "@mui/material/Autocomplete";
import { Unit } from "@lib/types/units";
import { getUnits } from "@lib/api/queries/units";
import {
  updateBinding,
  UpdateBindingFormData,
  updateBindingSchema,
} from "@lib/api/mutations/binding";
import { Binding, BindingResponse, RecurringType } from "@lib/types/binding";
import { BindingTypeOptions, weekdays } from "@lib/constants/general";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Plus, Trash2 } from "lucide-react";
import { getDaysLabel } from "@lib/utils/string";
import { differenceInHours, differenceInDays } from "date-fns";
import { UnitGroupsList } from "@lib/types/unit_group";
import { getUnitGroups } from "@lib/api/queries/unit_groups";
import { DriverGroupsList } from "@lib/types/driver_group";
import { getDriverGroups } from "@lib/api/queries/driver_groups";
interface Props {
  updateBindingDialogOpen: boolean;
  setUpdateBindingDialogOpen: (open: boolean) => void;
  refetch?: () => void;
  bindingData?: Binding;
}

const UpdateBinding: React.FC<Props> = ({
  updateBindingDialogOpen,
  setUpdateBindingDialogOpen,
  refetch,
  bindingData,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [duration, setDuration] = useState(1);
  const [durationUnit, setDurationUnit] = useState("hours");

  const {
    data: drivers,
    isFetching,
    error,
  } = useQuery<Driver[]>({
    queryKey: ["drivers"],
    queryFn: async () => {
      const drivers = await getDrivers();

      const filteredDrivers = drivers?.data.filter((driver) => !!driver.id);
      return filteredDrivers;
    },
    enabled: updateBindingDialogOpen,
    refetchOnWindowFocus: false,
  });

  const { data: units, error: unitsError } = useQuery<Unit[]>({
    queryKey: ["units"],
    queryFn: async () => {
      const units = await getUnits();

      return units?.units;
    },
    enabled: updateBindingDialogOpen,
    refetchOnWindowFocus: false,
  });

  const { data: unitGroups, error: unitGroupError } = useQuery<UnitGroupsList>({
    queryKey: ["unit-groups"],
    queryFn: async () => {
      return getUnitGroups();
    },
    enabled: updateBindingDialogOpen,
    refetchOnWindowFocus: false,
  });

  const { data: driverGroups, error: driverGroupError } =
    useQuery<DriverGroupsList>({
      queryKey: ["driver-groups"],
      queryFn: async () => {
        return getDriverGroups();
      },
      enabled: updateBindingDialogOpen,
      refetchOnWindowFocus: false,
    });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm<UpdateBindingFormData>({
    resolver: zodResolver(updateBindingSchema),
    defaultValues: {
      binding_type: bindingData?.binding_type || "",
      start_datetime: bindingData?.start_datetime
        ? new Date(bindingData.start_datetime)
        : undefined,
      end_datetime: bindingData?.end_datetime
        ? new Date(bindingData.end_datetime)
        : undefined,
      schedules: bindingData?.schedules || [],
    },
  });

  const bindingType = watch("binding_type");
  const schedules = watch("schedules");

  // Field Array for dynamic custom fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "schedules",
  });

  const { mutate, isPending: isUpdatingBinding } = useMutation({
    mutationFn: updateBinding,
    onSuccess: (data: BindingResponse) => {
      showSnackbar(t("user.bindings.editSuccess"), "success");

      setUpdateBindingDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const getInterval = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const hoursDiff = differenceInHours(endDate, startDate);
    const daysDiff = differenceInDays(endDate, startDate);

    if (daysDiff >= 1) {
      setDurationUnit("days");
      setDuration(daysDiff);
    } else {
      setDurationUnit("hours");
      setDuration(hoursDiff);
    }
  };

  useEffect(() => {
    if (bindingData) {
      setValue("binding_type", bindingData.binding_type);
      setValue(
        "start_datetime",
        bindingData.start_datetime
          ? new Date(bindingData.start_datetime)
          : undefined
      );
      setValue(
        "end_datetime",
        bindingData.end_datetime
          ? new Date(bindingData.end_datetime)
          : undefined
      );
      setValue(
        "schedules",
        bindingData.schedules?.map((schedule) => ({
          start_time: schedule.start_time || "",
          end_time: schedule.end_time || "",
          day_of_month: schedule.day_of_month,
          days_of_week: schedule.days_of_week,
          recurring_type: schedule.recurring_type,
        })) || []
      );

      if (bindingData.binding_type === "expiring") {
        getInterval(
          bindingData?.start_datetime || "",
          bindingData?.end_datetime || ""
        );
      }
    }
  }, [bindingData, updateBindingDialogOpen]);

  const handleUpdateBindings = (data: UpdateBindingFormData) => {
    if (bindingData) {
      mutate({ bindingId: bindingData.id, updateBindingParams: data });
    } else {
      showSnackbar(t("general.error"), "error");
    }
  };

  const handleDateChange = (newValue: Date | null, field: any) => {
    if (newValue) {
      const minutes = newValue.getMinutes();

      // Round minutes to the nearest multiple of 5
      const roundedMinutes = Math.round(minutes / 5) * 5;
      newValue.setMinutes(roundedMinutes); // Set the minutes to the rounded value
    }
    field.onChange(newValue || null); // Pass the new value to react-hook-form
  };

  const calculateDateRange = () => {
    const now = new Date();
    const end = new Date(now);
    if (durationUnit === "hours") {
      end.setHours(now.getHours() + duration);
    } else if (durationUnit === "days") {
      end.setDate(now.getDate() + duration);
    }

    setValue("start_datetime", now);
    setValue("end_datetime", end);
  };

  useEffect(() => {
    if (bindingType === "expiring") {
      calculateDateRange();
    } else {
      setValue("start_datetime", undefined);
      setValue("end_datetime", undefined);
    }
  }, [bindingType, setValue]);

  return (
    <FormDialog
      title={t("user.bindings.edit")}
      open={updateBindingDialogOpen}
      onClose={() => {
        setUpdateBindingDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleUpdateBindings)}
      submitText={t("general.save")}
      isSubmitting={isUpdatingBinding}
      submittingText={t("general.saving")}
    >
      {!isFetching && updateBindingDialogOpen && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="binding_type"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label={t("user.bindings.bindingType")}
                  placeholder={t("user.bindings.selectBindingType")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.binding_type}
                  helperText={errors.binding_type?.message}
                >
                  {BindingTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(`user.bindings.${option.label}`)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            {bindingData?.driver_id && (
              <div>
                <Autocomplete
                  options={drivers || []}
                  value={
                    drivers?.find(
                      (driver) => driver.id === bindingData?.driver_id
                    ) || null
                  }
                  readOnly
                  disabled
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("user.bindings.driver")}
                      variant="outlined"
                    />
                  )}
                />
              </div>
            )}

            {bindingData?.driver_group_id && (
              <div>
                <Autocomplete
                  options={driverGroups?.data || []}
                  value={
                    driverGroups?.data.find(
                      (driverGroup) =>
                        driverGroup.group_id === bindingData?.driver_group_id
                    ) || null
                  }
                  readOnly
                  disabled
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("user.bindings.driverGroup")}
                      variant="outlined"
                    />
                  )}
                />
              </div>
            )}

            {bindingData?.unit_id && (
              <div>
                <Autocomplete
                  options={units || []}
                  value={
                    units?.find(
                      (unit) => unit.unit_id === bindingData?.unit_id
                    ) || null
                  }
                  readOnly
                  disabled
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("user.bindings.unit")}
                      variant="outlined"
                    />
                  )}
                />
              </div>
            )}

            {bindingData?.unit_group_id && (
              <div>
                <Autocomplete
                  options={unitGroups?.data || []}
                  value={
                    unitGroups?.data.find(
                      (unitGroup) =>
                        unitGroup.group_id === bindingData?.unit_group_id
                    ) || null
                  }
                  readOnly
                  disabled
                  getOptionLabel={(option) => option.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("user.bindings.unitGroup")}
                      variant="outlined"
                    />
                  )}
                />
              </div>
            )}
            <Divider />

            <Grid2 container spacing={2} columns={{ xs: 1, md: 6 }}>
              {bindingType === "expiring" ? (
                <>
                  <Grid2 size={3}>
                    <TextField
                      label={t("user.bindings.duration")}
                      type="number"
                      value={duration}
                      onChange={(e) => {
                        setDuration(Number(e.target.value));

                        if (bindingType === "expiring") {
                          calculateDateRange();
                        }
                      }}
                      fullWidth
                      error={!!errors.start_datetime || !!errors.end_datetime}
                      helperText={errors.start_datetime?.message}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <FormControl fullWidth>
                      <InputLabel>{t("user.bindings.interval")}</InputLabel>
                      <Select
                        label={t("user.bindings.interval")}
                        value={durationUnit}
                        onChange={(e) => {
                          setDurationUnit(e.target.value);

                          if (bindingType === "expiring") {
                            calculateDateRange();
                          }
                        }}
                      >
                        <MenuItem value="hours">
                          {t("user.bindings.hours")}
                        </MenuItem>
                        <MenuItem value="days">
                          {t("user.bindings.days")}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid2>
                </>
              ) : (
                <>
                  <Grid2 size={3}>
                    <Controller
                      name="start_datetime"
                      control={control}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            {...field}
                            label={t("user.bindings.startDatetime")}
                            value={field.value || null} // Ensure null for no value
                            onChange={(newValue) =>
                              handleDateChange(newValue, field)
                            }
                            minDateTime={new Date()} // Ensure the date is not in the past
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                error: !!errors.start_datetime,
                                helperText: errors.start_datetime?.message,
                              },
                              actionBar: {
                                actions: ["clear", "accept"],
                              },
                              field: {
                                clearable: true,
                              },
                            }}
                            sx={{ width: "100%" }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </Grid2>
                  <Grid2 size={3}>
                    <Controller
                      name="end_datetime"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label={t("user.bindings.endDatetime")}
                          value={field.value || null} // Ensure null for no value
                          onChange={(newValue) =>
                            handleDateChange(newValue, field)
                          }
                          minDateTime={new Date()} // Ensure the date is not in the past
                          slotProps={{
                            textField: {
                              variant: "outlined",
                              error: !!errors.end_datetime,
                              helperText: errors.end_datetime?.message,
                            },
                            actionBar: {
                              actions: ["clear", "accept"],
                            },
                            field: {
                              clearable: true,
                            },
                          }}
                          sx={{ width: "100%" }}
                        />
                      )}
                    />
                  </Grid2>
                </>
              )}
            </Grid2>

            {bindingType === "recurring" && (
              <>
                <Divider />
                {/* Schedules Section */}
                <Box gap={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {t("user.bindings.schedules")}
                  </Typography>
                  {fields.map((field, index) => (
                    <Grid2
                      container
                      spacing={2}
                      columns={{ xs: 1, md: 6 }}
                      gap={2}
                      sx={{ mb: 2 }}
                    >
                      <Grid2 size={6}>
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          gap={1}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {t("user.bindings.schedule")} {index + 1}
                          </Typography>
                          <Divider sx={{ flexGrow: 1, marginY: 0 }} />
                          <IconButton
                            onClick={() => remove(index)}
                            color="error"
                            size="small"
                          >
                            <Trash2 size={16} />
                            <Typography variant="body2">
                              {t("user.bindings.remove")}
                            </Typography>
                          </IconButton>
                        </Box>
                      </Grid2>

                      <Grid2 size={3}>
                        <Controller
                          name={`schedules.${index}.recurring_type`}
                          control={control}
                          render={({ field }) => (
                            <FormControl sx={{ width: "100%" }}>
                              <InputLabel id={`recurring-type-label-${index}`}>
                                {t("user.bindings.recurringType")}
                              </InputLabel>

                              <Select
                                labelId={`recurring-type-label-${index}`}
                                id={`recurring-type-${index}`}
                                value={field.value}
                                onChange={(e) => {
                                  const newRecurringType = e.target.value;
                                  field.onChange(newRecurringType);

                                  setValue(
                                    `schedules.${index}.days_of_week`,
                                    undefined
                                  );
                                  setValue(
                                    `schedules.${index}.day_of_month`,
                                    undefined
                                  );
                                }}
                                input={
                                  <OutlinedInput
                                    id={`recurring-type-input-${index}`}
                                    label={t("user.bindings.recurringType")}
                                  />
                                }
                                renderValue={(selected) => (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {selected === RecurringType.WEEKLY
                                        ? t("Weekly")
                                        : t("Monthly")}
                                    </Typography>
                                  </Box>
                                )}
                              >
                                <MenuItem value="WEEKLY">
                                  {t("Weekly")}
                                </MenuItem>
                                <MenuItem value="MONTHLY">
                                  {t("Monthly")}
                                </MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid2>

                      <Grid2 size={3}>
                        {schedules?.[index]?.recurring_type ===
                          RecurringType.WEEKLY && (
                          <Controller
                            name={`schedules.${index}.days_of_week`}
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                sx={{ width: "100%" }}
                                error={
                                  !!errors?.schedules?.[index]?.days_of_week
                                }
                              >
                                <InputLabel id={`days-of-week-label-${index}`}>
                                  {t("user.bindings.daysOfWeek")}
                                </InputLabel>
                                <Select
                                  {...field} // Connect to React Hook Form
                                  labelId={`days-of-week-label-${index}`}
                                  id={`days-of-week-${index}`}
                                  multiple
                                  value={
                                    Array.isArray(field.value)
                                      ? field.value
                                      : []
                                  } // Ensure the value is always an array
                                  onChange={(event) => {
                                    const selectedValues = event.target
                                      .value as number[];
                                    field.onChange(selectedValues);
                                  }}
                                  input={
                                    <OutlinedInput
                                      id={`days-of-week-input-${index}`}
                                      label="Days of Week"
                                    />
                                  }
                                  renderValue={(selected) => (
                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography variant="body2">
                                        {getDaysLabel(selected)}
                                      </Typography>
                                    </Box>
                                  )}
                                >
                                  {Array.from({ length: 7 }, (_, i) => i).map(
                                    (day) => (
                                      <MenuItem key={day} value={day}>
                                        {weekdays[day]}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                                <FormHelperText>
                                  {
                                    errors?.schedules?.[index]?.days_of_week
                                      ?.message
                                  }
                                </FormHelperText>
                              </FormControl>
                            )}
                          />
                        )}
                        {schedules?.[index]?.recurring_type ===
                          RecurringType.MONTHLY && (
                          <>
                            <Controller
                              name={`schedules.${index}.day_of_month`}
                              control={control}
                              render={({ field }) => (
                                <FormControl sx={{ width: "100%" }}>
                                  <InputLabel
                                    id={`day-of-month-label-${index}`}
                                  >
                                    {t("user.bindings.daysOfMonth")}
                                  </InputLabel>
                                  <Select
                                    {...field} // Connect to React Hook Form
                                    labelId={`day-of-month-label-${index}`}
                                    id={`days-of-month-${index}`}
                                    value={field.value || ""} // Ensure value is defined
                                    onChange={(event) => {
                                      const selectedValue = event.target
                                        .value as number;
                                      field.onChange(selectedValue); // Update the value
                                    }}
                                    input={
                                      <OutlinedInput
                                        id={`day-of-month-input-${index}`}
                                        label={t("user.bindings.daysOfMonth")}
                                      />
                                    }
                                    renderValue={(selected) => (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {selected}
                                        </Typography>
                                      </Box>
                                    )}
                                  >
                                    {Array.from(
                                      { length: 31 },
                                      (_, i) => i + 1
                                    ).map((day) => (
                                      <MenuItem key={day} value={day}>
                                        {day}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            />
                          </>
                        )}
                      </Grid2>

                      <Grid2 size={3}>
                        <Controller
                          name={`schedules.${index}.start_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label={t("user.bindings.startTime")}
                              value={
                                field.value
                                  ? new Date(`1970-01-01T${field.value}`)
                                  : null
                              }
                              onChange={(value) => {
                                if (value) {
                                  const hours = String(
                                    value.getHours()
                                  ).padStart(2, "0");
                                  const minutes = String(
                                    value.getMinutes()
                                  ).padStart(2, "0");
                                  const seconds = String(
                                    value.getSeconds()
                                  ).padStart(2, "0");
                                  const timeString = `${hours}:${minutes}:${seconds}`; // Format to hh:mm:ss
                                  field.onChange(timeString); // Update with string value
                                } else {
                                  field.onChange(""); // Handle clearing the value
                                }
                              }}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  error:
                                    !!errors?.schedules?.[index]?.start_time,
                                  helperText:
                                    errors?.schedules?.[index]?.start_time
                                      ?.message,
                                  sx: {
                                    width: "100%",
                                  },
                                },
                              }}
                              ampm={false}
                            />
                          )}
                        />
                      </Grid2>

                      <Grid2 size={3}>
                        <Controller
                          name={`schedules.${index}.end_time`}
                          control={control}
                          render={({ field }) => (
                            <TimePicker
                              label={t("user.bindings.endTime")}
                              value={
                                field.value
                                  ? new Date(`1970-01-01T${field.value}`)
                                  : null
                              }
                              onChange={(value) => {
                                if (value) {
                                  const hours = String(
                                    value.getHours()
                                  ).padStart(2, "0");
                                  const minutes = String(
                                    value.getMinutes()
                                  ).padStart(2, "0");
                                  const seconds = String(
                                    value.getSeconds()
                                  ).padStart(2, "0");
                                  const timeString = `${hours}:${minutes}:${seconds}`; // Format to hh:mm:ss
                                  field.onChange(timeString); // Update with string value
                                } else {
                                  field.onChange(""); // Handle clearing the value
                                }
                              }}
                              ampm={false}
                              slotProps={{
                                textField: {
                                  variant: "outlined",
                                  error: !!errors?.schedules?.[index]?.end_time,
                                  helperText:
                                    errors?.schedules?.[index]?.end_time
                                      ?.message,
                                  sx: {
                                    width: "100%",
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid2>
                    </Grid2>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<Plus />}
                    onClick={() =>
                      append({
                        day_of_month: undefined,
                        days_of_week: undefined,
                        start_time: "",
                        end_time: "",
                        recurring_type: "weekly",
                      })
                    }
                  >
                    {t("user.bindings.addSchedule")}
                  </Button>

                  {errors.schedules?.message && (
                    <Alert
                      severity="error"
                      sx={{
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      {errors.schedules.message}
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default UpdateBinding;
